// product-variant-selector.js
// 导入自定义格式化价格函数
import { shopifyFormatPrice } from './custom-format-price.js';
/**
 * 获取选项的原始值（解码 HTML 实体）
 * @param {HTMLElement} option - 选项元素
 * @returns {string} 原始值
 */
function getOptionRawValue(option) {
  const value = option.getAttribute('data-value');
  if (!value) return '';
  
  // 解码 HTML 实体（将 &quot; 还原为 "，将 &#39; 还原为 ' 等）
  const textarea = document.createElement('textarea');
  textarea.innerHTML = value;
  return textarea.value;
}

class AccessoriesVariantSelector {
  constructor(container, accessoriesData) {
    this.container = container;           // 变体选择器容器
    this.productId = container.id.split('-').pop(); // 获取产品ID
    this.accessoriesData = accessoriesData;       // 配件数据（包含所有变体）
    this.currentVariant = null;           // 当前选中的变体
    this.customWrappers = [];             // 存储所有自定义下拉包装器
    this.selectedValues = [];             // 存储当前选中的值
    
    // 获取产品弹窗块
    this.productPopup = container.closest('.accessories-detail-block');

    // 获取产品item容器
    this.productItem = container.closest('.accessories_block-layout').querySelector(`.accessories-item[data-product-id="${this.productId}"]`);
    
    // 获取需要更新的元素
    this.pricePopupList = this.productPopup?.querySelector('.accessories-price-box');

    // 获取产品item中的价格元素
    this.priceItemList = this.productItem?.querySelector('.item-price-box');

    this.primaryImage = this.productItem?.querySelector('img');

    this.variantTitle = this.productItem?.querySelector('.variant-title');
    
    // 获取表单中的变体ID隐藏字段
    this.variantIdInput = this.productItem?.querySelector('input[data-action="bundle-add-to-cart"]');
    
    this.init();
  }
  
  /**
   * 初始化
   */
  init() {
    if (!this.accessoriesData || !this.accessoriesData.variants) {
      console.error('Accessory data not found for product:', this.productId);
      return;
    }
    
    // 获取所有自定义下拉包装器
    this.customWrappers = Array.from(this.container.querySelectorAll('.custom-variant-wrapper'));
    
    // 初始化选中的值
    this.initSelectedValues();
    
    // 计算并设置下拉触发器的最小宽度
    this.setTriggerMinWidth();
    
    // 获取当前选中的变体+
    this.currentVariant = this.getCurrentVariant();
    
    // 更新表单中的变体ID
    this.updateVariantIdInput();
    
    // 更新 label 中的显示值
    this.updateLabelDisplayValues();
    
    // 更新其他下拉的可用选项（联动）
    this.updateDropdownsAvailability();
    
    // 绑定事件
    this.bindEvents();
    
    // 更新UI（价格、图片等）
    this.updateUI();
    
    // 触发初始化完成事件
    this.dispatchEvent('selector:init', { variant: this.currentVariant });
  }


  /**
   * 计算并设置下拉触发器的最小宽度
   * 确保custom-variant-trigger的宽度在折叠时不变动
   */
  setTriggerMinWidth() {
    this.customWrappers.forEach((wrapper) => {
      const trigger = wrapper.querySelector('.custom-variant-trigger');
      if (!trigger) return;
      
      // 获取所有选项
      const options = wrapper.querySelectorAll('.custom-variant-option');
      if (options.length === 0) return;
      
      // 获取箭头元素
      const arrow = wrapper.querySelector('.custom-variant-arrow');
      // 计算箭头宽度
      let arrowWidth = 0;
      if (arrow) {
        arrowWidth = arrow.offsetWidth;
      }
      
      // 获取gap值
      let gap = 0;
      const computedStyle = window.getComputedStyle(trigger);
      const gapValue = computedStyle.gap || computedStyle.columnGap || computedStyle.rowGap;
      if (gapValue) {
        gap = parseInt(gapValue) || 0;
      }
      
      // 创建临时元素来测量文本宽度
      const tempElement = document.createElement('div');
      tempElement.style.position = 'absolute';
      tempElement.style.visibility = 'hidden';
      tempElement.style.whiteSpace = 'nowrap';
      tempElement.style.font = computedStyle.font;
      tempElement.style.padding = computedStyle.padding;
      tempElement.style.border = computedStyle.border;
      document.body.appendChild(tempElement);
      
      // 计算最长选项的宽度
      let maxWidth = 0;
      options.forEach((option) => {
        tempElement.textContent = option.textContent;
        const width = tempElement.offsetWidth;
        if (width > maxWidth) {
          maxWidth = width;
        }
      });

      // 加上箭头宽度、gap值和缓冲区宽度
      maxWidth += arrowWidth + gap;
      
      // 获取父级元素的宽度
      const parentElement = trigger.parentElement;
      if (parentElement) {
        const parentWidth = parentElement.offsetWidth;
        // 如果maxWidth大于父级宽度，使用父级宽度
        if (maxWidth > parentWidth) {
          maxWidth = parentWidth;
        }
      }
      
      // 为触发器设置最小宽度
      if (maxWidth > 0) {
        trigger.style.minWidth = `${maxWidth}px`;
      }
      
      // 移除临时元素
      document.body.removeChild(tempElement);
    });
  }

  /**
   * 初始化选中的值
   */
  initSelectedValues() {
    this.selectedValues = [];
    this.customWrappers.forEach((wrapper, index) => {
      const selectedOption = wrapper.querySelector('.custom-variant-option.selected');
      if (selectedOption) {
        this.selectedValues[index] = getOptionRawValue(selectedOption);
      } else {
        const firstOption = wrapper.querySelector('.custom-variant-option');
        if (firstOption) {
          this.selectedValues[index] = getOptionRawValue(firstOption);
          firstOption.classList.add('selected');
          const selectedSpan = wrapper.querySelector('.custom-variant-selected');
          if (selectedSpan) {
            selectedSpan.textContent = firstOption.textContent;
          }
        }
      }
    });
  }
  
  /**
   * 获取当前选中的变体
   */
  getCurrentVariant() {
    // 过滤掉未定义的值
    const selectedValues = this.selectedValues.filter(v => v !== undefined && v !== null);
    
    if (selectedValues.length === 0) {
      return this._getFirstAvailableVariant();
    }
    
    // 查找匹配的变体
    const variant = this.accessoriesData.variants.find(v => {
      // 方法1：如果变体有 options 数组（新版API格式）
      if (v.options && Array.isArray(v.options)) {
        return this._compareVariantOptions(v.options, selectedValues);
      }
      
      // 方法2：回退到旧版 option1/option2/option3 字段
      const oldStyleMatch = this._compareOldStyleVariant(v, selectedValues);
      if (oldStyleMatch !== null) return oldStyleMatch;
      
      return false;
    });
    
    return variant || this._getFirstAvailableVariant();
  }
  
  /**
   * 通过 options 数组比较变体
   */
  _compareVariantOptions(variantOptions, selectedValues) {
    if (!variantOptions || variantOptions.length !== selectedValues.length) {
      return false;
    }
    
    for (let i = 0; i < selectedValues.length; i++) {
      if (variantOptions[i] !== selectedValues[i]) {
        return false;
      }
    }
    
    return true;
  }
  
  /**
   * 通过旧版 option1/option2/option3 字段比较
   */
  _compareOldStyleVariant(variant, selectedValues) {
    const hasOption1 = 'option1' in variant;
    const hasOption2 = 'option2' in variant;
    const hasOption3 = 'option3' in variant;
    
    if (!hasOption1 && !hasOption2 && !hasOption3) {
      return null;
    }
    
    const oldStyleOptions = [];
    if (hasOption1) oldStyleOptions[0] = variant.option1;
    if (hasOption2) oldStyleOptions[1] = variant.option2;
    if (hasOption3) oldStyleOptions[2] = variant.option3;
    
    const filteredOldOptions = oldStyleOptions.filter(opt => opt !== null && opt !== undefined);
    
    if (filteredOldOptions.length !== selectedValues.length) {
      return false;
    }
    
    for (let i = 0; i < selectedValues.length; i++) {
      if (filteredOldOptions[i] !== selectedValues[i]) {
        return false;
      }
    }
    
    return true;
  }
  
  /**
   * 获取第一个可用的变体
   */
  _getFirstAvailableVariant() {
    const availableVariant = this.accessoriesData.variants.find(v => v.available);
    return availableVariant || this.accessoriesData.variants[0];
  }
  
  /**
   * 获取变体的选项值数组
   */
  _getVariantOptionsArray(variant) {
    if (variant.options && Array.isArray(variant.options)) {
      return variant.options;
    }
    
    const oldStyle = [];
    if (variant.option1) oldStyle.push(variant.option1);
    if (variant.option2) oldStyle.push(variant.option2);
    if (variant.option3) oldStyle.push(variant.option3);
    
    return oldStyle.length > 0 ? oldStyle : null;
  }
  
  /**
   * 更新 label 中的显示值
   */
  updateLabelDisplayValues() {
    this.customWrappers.forEach((wrapper) => {
      const variantOptionDiv = wrapper.closest('.variant-option');
      const selectValueSpan = variantOptionDiv?.querySelector('.select-value');
      const selectedSpan = wrapper.querySelector('.custom-variant-selected');
      
      if (selectValueSpan && selectedSpan) {
        selectValueSpan.textContent = selectedSpan.textContent;
      }
    });
  }

  /**
   * 更新下拉选项的可用状态（联动）
   */
  updateDropdownsAvailability() {
    this.customWrappers.forEach((wrapper, position) => {
      const options = wrapper.querySelectorAll('.custom-variant-option');
      
      options.forEach(option => {
        const optionValue = getOptionRawValue(option);
        
        const isAvailable = this._isOptionValueAvailable(optionValue, position, this.selectedValues);
        
        if (!isAvailable) {
          option.style.opacity = '0.4';
          option.style.cursor = 'not-allowed';
          option.style.pointerEvents = 'none';
          option.classList.add('disabled');
        } else {
          option.style.opacity = '';
          option.style.cursor = '';
          option.style.pointerEvents = '';
          option.classList.remove('disabled');
        }
      });
      
      // 检查当前选中的值是否还可用
      const currentSelectedValue = this.selectedValues[position];
      if (currentSelectedValue) {
        let currentOption = null;
        for (const option of wrapper.querySelectorAll('.custom-variant-option')) {
          if (getOptionRawValue(option) === currentSelectedValue) {
            currentOption = option;
            break;
          }
        }
        
        if (currentOption && (currentOption.style.pointerEvents === 'none' || currentOption.classList.contains('disabled'))) {
          const firstAvailable = wrapper.querySelector('.custom-variant-option:not(.disabled)');
          if (firstAvailable) {
            this.selectOption(wrapper, firstAvailable, position);
          }
        }
      }
    });
    
    const previousVariant = this.currentVariant;
    this.currentVariant = this.getCurrentVariant();
    
    if (!previousVariant || previousVariant.id !== this.currentVariant?.id) {
      this.updateVariantIdInput();
      this.updateUI();
    }
  }
  
  /**
   * 检查某个选项值是否可用
   */
  _isOptionValueAvailable(value, position, currentSelectedValues) {
    const testValues = [...currentSelectedValues];
    testValues[position] = value;
    
    const filteredTestValues = testValues.filter(v => v !== undefined && v !== null);
    
    return this.accessoriesData.variants.some(variant => {
      const variantOptions = this._getVariantOptionsArray(variant);
      
      if (!variantOptions || variantOptions.length !== filteredTestValues.length) {
        return false;
      }
      
      for (let i = 0; i < filteredTestValues.length; i++) {
        if (variantOptions[i] !== filteredTestValues[i]) {
          return false;
        }
      }
      
      return variant.available;
    });
  }
  
  /**
   * 选择选项
   */
  selectOption(wrapper, option, position) {
    const selectedValue = getOptionRawValue(option);
    const selectedText = option.textContent;
    
    this.selectedValues[position] = selectedValue;
    
    const selectedSpan = wrapper.querySelector('.custom-variant-selected');
    if (selectedSpan) {
      selectedSpan.textContent = selectedText;
    }
    
    wrapper.querySelectorAll('.custom-variant-option').forEach(opt => {
      opt.classList.remove('selected');
    });
    option.classList.add('selected');
    
    const variantOptionDiv = wrapper.closest('.variant-option');
    const selectValueSpan = variantOptionDiv?.querySelector('.select-value');
    if (selectValueSpan) {
      selectValueSpan.textContent = selectedText;
    }
    
    const previousVariant = this.currentVariant;
    this.currentVariant = this.getCurrentVariant();
    
    this.updateDropdownsAvailability();
    this.updateVariantIdInput();
    
    if (!previousVariant || previousVariant.id !== this.currentVariant?.id) {
      this.updateUI();
    }
    
    this.dispatchEvent('variant:changed', {
      variant: this.currentVariant,
      previousVariant: previousVariant,
      selectedValues: this.selectedValues
    });
  }
  
  /**
   * 更新表单中的变体ID隐藏字段
   */
  updateVariantIdInput() {
    if (this.variantIdInput && this.currentVariant) {
      const productVariantIdValue = this.variantIdInput.getAttribute('data-product-variant-id');
      const newValue = this.currentVariant.id;
      
      if (productVariantIdValue !== String(newValue)) {
        this.variantIdInput.value = newValue;
        this.variantIdInput.setAttribute('data-product-variant-id', newValue);
        this.variantIdInput.dispatchEvent(new Event('change', { bubbles: true }));
        this.variantIdInput.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }
  }
  
  /**
   * 关闭所有下拉列表
   */
  closeAllDropdowns() {
    document.querySelectorAll('.custom-variant-dropdown.show').forEach(dropdown => {
      dropdown.classList.remove('show');
    });
    document.querySelectorAll('.custom-variant-trigger.active').forEach(trigger => {
      trigger.classList.remove('active');
    });
  }
  
  /**
   * 初始化自定义下拉事件
   */
  initCustomDropdownEvents() {
    this.customWrappers.forEach((wrapper, index) => {
      const trigger = wrapper.querySelector('.custom-variant-trigger');
      const dropdown = wrapper.querySelector('.custom-variant-dropdown');
      
      if (!trigger || !dropdown) return;
      
      const newTrigger = trigger.cloneNode(true);
      trigger.parentNode.replaceChild(newTrigger, trigger);
      
      const finalTrigger = wrapper.querySelector('.custom-variant-trigger');
      const finalDropdown = wrapper.querySelector('.custom-variant-dropdown');
      
      finalTrigger.addEventListener('click', (e) => {
        e.stopPropagation();
        const willBeOpen = !finalDropdown.classList.contains('show');
        // 先关闭所有
        this.closeAllDropdowns();
        
        // 如果需要打开，再打开当前
        if (willBeOpen) {
            finalDropdown.classList.add('show');
            finalTrigger.classList.add('active');
        }
      });
      
      const options = wrapper.querySelectorAll('.custom-variant-option');
      options.forEach(option => {
        const newOption = option.cloneNode(true);
        option.parentNode.replaceChild(newOption, option);
        
        newOption.addEventListener('click', (e) => {
          e.stopPropagation();
          
          if (newOption.classList.contains('disabled') || newOption.style.pointerEvents === 'none') {
            return;
          }
          
          this.selectOption(wrapper, newOption, index);
          
          finalDropdown.classList.remove('show');
          finalTrigger.classList.remove('active');
        });
      });
    });
    
    if (!window._customDropdownGlobalHandler) {
      window._customDropdownGlobalHandler = true;
      document.addEventListener('click', () => {
        this.closeAllDropdowns();
      });
    }
  }
  
  /**
   * 更新UI（价格、图片等）
   */
  updateUI() {
    if (!this.currentVariant) return;
    this.updatePrice();
    this.updateImages();
    this.updateVariantTitle();
  }
  /**
   * 更新变体标题
   */
  updateVariantTitle() {
    if (!this.variantTitle || !this.currentVariant) return;

    const title = this.currentVariant.title;
    if (title && title !== 'Default Title') {
      this.variantTitle.textContent = title;
    }
  }
  
  /**
   * 更新价格显示
   */
  updatePrice() {
    if (!this.pricePopupList) return;
    
    const currentPrice = this.currentVariant.price;
    const compareAtPrice = this.currentVariant.compare_at_price;
    
    const formattedCurrentPrice = this.formatPrice(currentPrice);
    const formattedComparePrice = compareAtPrice ? this.formatPrice(compareAtPrice) : null;
    
    const hasDiscount = compareAtPrice && compareAtPrice > currentPrice;
    
    let PopupPriceHighlight = this.pricePopupList.querySelector('.sales-price');
    let PopupPriceCompare = this.pricePopupList.querySelector('.underlined-price');

    let ItemPriceHighlight = this.priceItemList.querySelector('.item-sale-price');
    let ItemPriceCompare = this.priceItemList.querySelector('.item-underlined-price');
    
    if (hasDiscount) {
      if (PopupPriceHighlight && PopupPriceCompare) {
        this._updatePriceNumber(PopupPriceHighlight, formattedCurrentPrice);
        this._updatePriceNumber(PopupPriceCompare, formattedComparePrice);

        this._updatePriceNumber(ItemPriceHighlight, formattedCurrentPrice);
        this._updatePriceNumber(ItemPriceCompare, formattedComparePrice);

      } else if (PopupPriceHighlight && !PopupPriceCompare) {
        this._updatePriceNumber(PopupPriceHighlight, formattedCurrentPrice);
        this._updatePriceNumber(ItemPriceHighlight, formattedCurrentPrice);

        this._createComparePrice(formattedComparePrice);
      } else if (!PopupPriceHighlight && PopupPriceCompare) {
        this._updatePriceNumber(PopupPriceCompare, formattedComparePrice);
        this._updatePriceNumber(ItemPriceCompare, formattedComparePrice);
        this._createHighlightPrice(formattedCurrentPrice);
      } else {
        this._createFullPriceStructure(formattedCurrentPrice, formattedComparePrice);
      }
    } else {
      let priceElement = this.pricePopupList.querySelector('.sales-price');
      let itemPriceElement = this.priceItemList.querySelector('.item-sale-price');
      
      if (priceElement) {
        this._updatePriceNumber(priceElement, formattedCurrentPrice);
        this._updatePriceNumber(itemPriceElement, formattedCurrentPrice);
        if (PopupPriceCompare) {
          PopupPriceCompare.remove();
          itemPriceCompare.remove();
        }
      } else {
        this._createSinglePrice(formattedCurrentPrice);
      }
    }
  }
  
  /**
   * 更新价格数字
   */
  _updatePriceNumber(priceElement, newPrice) {
    const childNodes = priceElement.childNodes;
    
    for (let i = 0; i < childNodes.length; i++) {
      const node = childNodes[i];
      if (node.nodeType === Node.TEXT_NODE && node.textContent.trim()) {
        const trimmedText = node.textContent.trim();
        if (/[\d.,€$£¥]/.test(trimmedText)) {
          node.textContent = newPrice;
          return;
        }
      }
    }
    
    const lastChild = priceElement.lastChild;
    if (lastChild && lastChild.nodeType === Node.TEXT_NODE) {
      lastChild.textContent = newPrice;
      return;
    }
    
    priceElement.appendChild(document.createTextNode(newPrice));
  }
  
  /**
   * 创建折扣价元素
   */
  _createHighlightPrice(price) {
    const salePriceText = this._getPriceText('sale');
    const newPriceElement = document.createElement('span');
    newPriceElement.className = 'sales-price';
    newPriceElement.innerHTML = `
      <span class="visually-hidden">${salePriceText}</span>${price}
    `;
    
    const priceCompare = this.pricePopupList.querySelector('.underlined-price');
    if (priceCompare) {
      this.pricePopupList.insertBefore(newPriceElement, priceCompare);
      this.priceItemList.insertBefore(newPriceElement, priceCompare);
    } else {
      this.pricePopupList.appendChild(newPriceElement);
      this.priceItemList.appendChild(newPriceElement);
    }
  }
  
  /**
   * 创建对比价元素
   */
  _createComparePrice(price) {
    const regularPriceText = this._getPriceText('regular');
    const newCompareElement = document.createElement('span');
    newCompareElement.className = 'underlined-price';
    newCompareElement.innerHTML = `
      <span class="visually-hidden">${regularPriceText}</span>${price}
    `;
    this.pricePopupList.appendChild(newCompareElement);
    this.priceItemList.appendChild(newCompareElement);
  }
  
  /**
   * 创建完整价格结构
   */
  _createFullPriceStructure(salePrice, regularPrice) {
    const salePriceText = this._getPriceText('sale');
    const regularPriceText = this._getPriceText('regular');
    
    this.pricePopupList.innerHTML = '';
    this.priceItemList.innerHTML = '';
    
    const highlightElement = document.createElement('span');
    highlightElement.className = 'sales-price';
    highlightElement.innerHTML = `
      <span class="visually-hidden">${salePriceText}</span>${salePrice}
    `;
    
    const compareElement = document.createElement('span');
    compareElement.className = 'underlined-price';
    compareElement.innerHTML = `
      <span class="visually-hidden">${regularPriceText}</span>${regularPrice}
    `;
    
    this.pricePopupList.appendChild(highlightElement);
    this.pricePopupList.appendChild(compareElement);

    this.priceItemList.appendChild(highlightElement);
    this.priceItemList.appendChild(compareElement);
  }
  
  /**
   * 创建单个价格结构
   */
  _createSinglePrice(price) {
    const salePriceText = this._getPriceText('sale');
    this.pricePopupList.innerHTML = '';
    this.priceItemList.innerHTML = '';
    
    const priceElement = document.createElement('span');
    priceElement.className = 'sales-price';
    priceElement.innerHTML = `
      <span class="visually-hidden">${salePriceText}</span>${price}
    `;
    this.pricePopupList.appendChild(priceElement);
    this.priceItemList.appendChild(priceElement);
  }
  
  /**
   * 获取价格文案
   */
  _getPriceText(type) {
    if (type === 'sale') {
      return window.languages?.productSalePrice || 
             window.theme?.translations?.sale_price || 
             'Sale price';
    } else {
      return window.languages?.productRegularPrice || 
             window.theme?.translations?.regular_price || 
             'Regular price';
    }
  }
  /**
   * 格式化价格
   * 使用 shopifyFormatPrice
   */
    formatPrice(priceInCents) {
        return shopifyFormatPrice(priceInCents);
    }

  /**
   * 更新产品图片
   */
  updateImages() {
    if (!this.currentVariant) return;
    
    const variantImage = this.currentVariant.featured_image || this.currentVariant.image;
    
    if (variantImage && variantImage.src) {
      if (this.primaryImage) {
        const imageUrl = this._getOptimizedImageUrl(variantImage.src, 100);
        this.primaryImage.src = imageUrl;
        this.primaryImage.srcset = `${imageUrl} 100w`;

        this.primaryImage.style.width = '50px';
        this.primaryImage.style.height = 'auto';
        
        if (variantImage.alt) {
          this.primaryImage.alt = variantImage.alt;
        }
        
        this.primaryImage.classList.add('image--fade-in');
        setTimeout(() => {
          this.primaryImage.classList.remove('image--fade-in');
        }, 100);

      }
      // 切换swiper图片
      this.updateSwiperImage(variantImage.id);
    } else {
      if (this.accessoriesData.images && this.accessoriesData.images.length > 0 && this.primaryImage) {
        const firstImage = this.accessoriesData.images[0];
        const imageUrl = this._getOptimizedImageUrl(firstImage.src, 600);
        this.primaryImage.src = imageUrl;
        this.primaryImage.srcset = `${imageUrl} 600w`;
        this.primaryImage.style.width = '50px';
        this.primaryImage.style.height = 'auto';    

      }
    }
  }

  updateSwiperImage(mediaId) {
    if (!mediaId) return;

    const key = this.productId;
    const swipers = window.__accessories_swipers && window.__accessories_swipers[key];

    if (swipers && swipers.mainSwiper && swipers.mainSwiper.slides && swipers.mainSwiper.slides.length > 0) {
      const slides = swipers.mainSwiper.slides;
      for (let i = 0; i < slides.length; i++) {
        const slideMediaId = slides[i].getAttribute('data-media-id');
        if (slideMediaId == mediaId) {
          if (swipers.mainSwiper.activeIndex !== i) {
            swipers.mainSwiper.slideTo(i);
          }
          break;
        }
      }
    } else {
      window.__accessories_pending_media = window.__accessories_pending_media || {};
      window.__accessories_pending_media[key] = mediaId;
    }
  }
  
  /**
   * 获取优化后的图片URL
   */
  _getOptimizedImageUrl(url, size = 100) {
    if (url && url.includes('cdn.shopify.com')) {
      const baseUrl = url.split('?')[0];
      return `${baseUrl}?v=${Date.now()}&width=${size}`;
    }
    return url;
  }
  
  /**
   * 触发自定义事件
   */
  dispatchEvent(eventName, detail = {}) {
    const event = new CustomEvent(eventName, {
      bubbles: true,
      detail: { ...detail, selector: this }
    });
    this.container.dispatchEvent(event);
  }
  
  /**
   * 绑定事件监听
   */
  bindEvents() {
    this.initCustomDropdownEvents();
    
    // 监听窗口大小变化，重新计算触发器宽度
    this._resizeHandler = this.setTriggerMinWidth.bind(this);
    window.addEventListener('resize', this._resizeHandler);
  }
  
  /**
   * 销毁实例
   */
  destroy() {
    if (window._customDropdownGlobalHandler) {
      delete window._customDropdownGlobalHandler;
    }
    
    // 移除窗口大小变化的事件监听器
    if (this._resizeHandler) {
      window.removeEventListener('resize', this._resizeHandler);
    }
  }
}


/**
 * 初始化所有产品变体选择器
 */
function initAccessoriesVariantSelectors() {
  const selectors = document.querySelectorAll('.accessories-detail-block .product-item__variant-selector-wrapper');
  
  selectors.forEach(container => {
    const productPopup = container.closest('.accessories-detail-block');
    const productJsonElement = productPopup?.querySelector('[data-accessories-json]');
    
    if (productJsonElement) {
      try {
        const accessoriesData = JSON.parse(productJsonElement.innerHTML);
        
        if (!container._variantSelector) {
          container._variantSelector = new AccessoriesVariantSelector(container, accessoriesData);
        }
       
      } catch (error) {
        console.error('Failed to parse accessories JSON:', error);
      }
    } else {
      console.warn('Accessories JSON not found for selector:', container);
    }
  });
}

/**
 * 清理所有选择器实例
 */
function destroyAllAccessoriesVariantSelectors() {
  const selectors = document.querySelectorAll('.accessories-detail-block .product-item__variant-selector-wrapper');
  selectors.forEach(container => {
    if (container._variantSelector) {
      container._variantSelector.destroy();
      delete container._variantSelector;
    }
  });
}

/**
 * 使用MutationObserver监听DOM变化
 */
function observeAccessoriesListChanges() {
  const productListContainer = document.querySelector('.accessories_block-layout');
  
  if (!productListContainer) {
    console.warn('Accessories list container not found');
    return;
  }
  
  const observer = new MutationObserver((mutations) => {
    let shouldReinit = false;
    
    mutations.forEach(mutation => {
      if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
        mutation.addedNodes.forEach(node => {
          if (node.nodeType === 1) {
            if (node.classList?.contains('accessories-detail-block') || 
                node.querySelector?.('.accessories-detail-block')) {
              shouldReinit = true;
            }
          }
        });
      }
    });
    
    if (shouldReinit) {
      setTimeout(() => {
        initAccessoriesVariantSelectors();
      }, 100);
    }
  });
  
  const observerConfig = {
    childList: true,
    subtree: true
  };
  
  observer.observe(productListContainer, observerConfig);
  return observer;
}



// 页面加载完成后初始化
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initAccessoriesVariantSelectors();
    window._AccessoriesVariantSelectorObserver = observeAccessoriesListChanges();
  });
} else {
  initAccessoriesVariantSelectors();
  window._AccessoriesVariantSelectorObserver = observeAccessoriesListChanges();
}
